'use client'

import type { ComponentType } from 'react'

import dynamic from 'next/dynamic'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import { DetailsValue } from '@/buyer-portal/components/features/shared'
import { HeaderAlignedPageWidth } from '@/components/PageWidth'
import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'
import { signUpCtaLocationForTab } from '@/helpers/telemetry/signupLinkTracking'

import { useStorefrontId } from '../StorefrontContext'
import { lexicalHasContent } from './cmsSellerProfileAbout'
import { SellerProfileLexicalRichText } from './SellerProfileLexicalRichText'
import { Container, InfoSection, StorefrontTableContainer, Title } from './ui'

// Dynamic so a visitor only downloads the one seller body their storefront resolves to.
const TmoDeviceUnlockingTermsDetails = dynamic(
  () => import('./tmoDeviceUnlockingTermsDetails')
)

/**
 * Storefront ids are per environment, so a seller is listed once per environment it exists in.
 * Same key the LaunchDarkly `deviceUnlockingTermsEnabledStorefrontIds` allowlist uses.
 *
 * A storefront the allowlist or the CMS toggle turns the tab on for but that is absent here has no
 * body of its own — it must not borrow another seller's, so it renders the empty state. Before
 * this map every such storefront published T-Mobile's carrier terms under its own name.
 */
const LEGACY_DEVICE_UNLOCKING_BY_STOREFRONT_ID: Record<string, ComponentType> =
  {
    // T-Mobile — dev, staging, prod. No qa storefront exists for this seller.
    '67d41cd1f27ef0e825a2fdaf': TmoDeviceUnlockingTermsDetails,
    '6a63f164fe43dbbe52e2b180': TmoDeviceUnlockingTermsDetails,
    '6a863bc02f2e4d913d44fcfa': TmoDeviceUnlockingTermsDetails,
  }

export default function DeviceUnlockingTermsContent({
  cmsSellerProfile,
}: {
  /** CMS row for this seller; its unlocking terms replace the hardcoded content when filled in. */
  cmsSellerProfile?: CmsSellerProfile | null
}) {
  const storefrontId = useStorefrontId()
  const cms = cmsSellerProfile
  const showCms = Boolean(cms && lexicalHasContent(cms.deviceUnlockingContent))
  const LegacyDetails = storefrontId
    ? LEGACY_DEVICE_UNLOCKING_BY_STOREFRONT_ID[storefrontId]
    : undefined

  return (
    <HeaderAlignedPageWidth>
      <StorefrontTableContainer>
        <Title>
          <FormattedMessage id="Storefront.deviceUnlockingTerms.title" />
        </Title>
        <InfoSection>
          <DetailsValue>
            {showCms && cms ? (
              <Container>
                <SellerProfileLexicalRichText
                  data={cms.deviceUnlockingContent}
                  signUpCtaLocation={signUpCtaLocationForTab(
                    cms.sellerSlug,
                    'device-unlocking-terms'
                  )}
                />
              </Container>
            ) : LegacyDetails ? (
              <LegacyDetails />
            ) : (
              <Container>
                <p>
                  <FormattedMessage id="Storefront.deviceUnlockingTerms.emptyCms" />
                </p>
              </Container>
            )}
          </DetailsValue>
        </InfoSection>
      </StorefrontTableContainer>
    </HeaderAlignedPageWidth>
  )
}
